/* FORMULARIOS DETECTADOS — extracción determinística del layout real (SIN IA).
   Usa el "Convertidor SST a Script v2" (vendor, hecho a mano por el equipo) para
   sacar de las WINDOW/MENU del fuente: labels posicionales EXACTOS, tipo de control
   (#D=select con opciones, #C=checkbox), obligatoriedad, windowType (form/confirmation/
   message), botones [@BTN_x] y el ROL de cada variable por su USO real:
     user_input    → campo visible que llena el vendedor
     read_display  → dato leído del PNR que solo se muestra (NO escribe)
     read_only / read_prefilled → dato interno
     unused        → ni generarlo
   El draft se inyecta al prompt del Agente 1/3 (bloque FORMULARIOS_DETECTADOS) para que
   la IA deje de adivinar el form y solo mapee al schema Boston. */

import { parseSst } from './sstFormsVendor/sstParser.js'
import { analyzeSemantics } from './sstFormsVendor/semanticAnalyzer.js'
import { analyzeVariables } from './sstFormsVendor/variableAnalyzer.js'
import { analyzeControlTypes } from './sstFormsVendor/controlTypeAnalyzer.js'
import { analyzeVariableState } from './sstFormsVendor/variableStateAnalyzer.js'
import { analyzeFlow } from './sstFormsVendor/flowAnalyzer.js'
import { analyzeActions } from './sstFormsVendor/actionAnalyzer.js'
import { analyzeBlocks } from './sstFormsVendor/blockAnalyzer.js'
import { generateDataJson } from './sstFormsVendor/dataJsonGenerator.js'
import { normalizeReplacementChars } from './parsers'
import { preprocessSst } from './triage'

const MAX_FORMS = 6
const MAX_FIELDS_POR_FORM = 24
const MAX_OPCIONES = 6
const MAX_BLOCK_CHARS = 4200

function runChain(text, name) {
  const po = parseSst(text, name)
  const irSem = analyzeSemantics(po.ast, name)
  const irBase = analyzeControlTypes(irSem)
  // BASE: forms/labels/controles/windowType ANTES del analisis de uso.
  // (analyzeVariables ve los comandos »...« —que nosotros sí destapamos— y
  // degrada inputs de WINDOW usados en comandos a readonly: la WINDOW manda.)
  const dataJsonBase = generateDataJson(irBase)
  let ir = analyzeVariables(po.ast, irBase)
  ir = analyzeVariableState(po.ast, ir)
  const fa = analyzeFlow(po.ast, ir)
  ir = analyzeActions(po.ast, fa.ir)
  ir = analyzeBlocks(po.ast, ir)
  return { ir, dataJson: dataJsonBase }
}

function mapField(f) {
  const esInput = f.visible !== false && !f.readonly && !f.displayOnly && f.typedata !== 'label' && f.typedata !== 'button'
  const out = {
    label: String(f.label || '').trim(),
    variable: f.binding || f.name || f.variable || '',
    control: f.typedata || f.type || 'text',
    mandatory: Boolean(f.mandatory),
    visible: f.visible !== false,
    readonly: Boolean(f.readonly || f.displayOnly),
    rol: esInput ? 'user_input' : (f.classification || ''),
  }
  if (f.maxlength) out.maxlength = f.maxlength
  if (Array.isArray(f.datasource) && f.datasource.length) {
    out.opciones = f.datasource.map((o) => (o && o.text != null ? String(o.text) : String(o))).filter(Boolean)
  }
  // grupos de checkboxes (#C): el vendor expone las variables miembro en options[]
  if (Array.isArray(f.options) && f.options.length && f.options[0] && f.options[0].binding) {
    out.miembros = f.options.map((o) => `[${o.binding || o.name}] «${String(o.text || '').replace(/#[A-Z]$/i, '')}»`)
    out.control = 'checkbox_group'
  }
  return out
}

/* === Scanner PROPIO de MENUs ===
   El vendor solo captura menus estilo "texto + GOTO/ventana". Los .sst reales del
   corpus (Fact_clientesvarios) usan: item + comandos »...« inline por rama, o
   item + IF [V] <> " " THEN [V] = val ENDIF (asignacion), y MENUs ANIDADOS.
   Este scanner cubre ambos y alimenta el bloque con la rama COMPLETA. */
const RX_MENU = /^\s*MENU\b[^"]*H="([^"]+)"/i
const RX_ENDMENU = /^\s*ENDMENU\b/i
const RX_CHEVRON = /»(.+?)«/
const RX_ASSIGN = /^\s*IF\s*\[\w+\]\s*<>\s*"[^"]*"\s*THEN\s*\[(\w+)\]\s*=\s*([^\s].*?)\s*ENDIF/i
const RX_GOTO = /^\s*GOTO\s+([A-Z0-9_]+)/i
const RX_KEYWORD = /^\s*(DEFINE|DEFAULT|WINDOW|ENDWINDOW|READ|WRITE|REQUIRE|COMPUTE|EXIT|IF|ENDIF|ELSE|SUB|ENDSUB|CALL|BOTONES)\b|^\s*[A-Z0-9_]+:\s*$/i

export function scanMenus(preprocessedText) {
  const lines = String(preprocessedText || '').split(/\r?\n/)
  const top = []
  const stack = [] // [{menu, currentItem}]
  let windowDepth = 0 // contenido de WINDOW dentro de una rama: NO son items del menu
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '')
    if (/^\s*ENDWINDOW\b/i.test(line)) {
      windowDepth = Math.max(0, windowDepth - 1)
      continue
    }
    if (/^\s*WINDOW\b/i.test(line)) {
      windowDepth++
      continue
    }
    if (windowDepth > 0) continue
    if (/^\s*;/.test(line)) continue // comentario Scribe
    const mMenu = line.match(RX_MENU)
    if (mMenu) {
      const menu = { title: mMenu[1].trim(), items: [] }
      const parent = stack[stack.length - 1]
      if (parent && parent.currentItem) parent.currentItem.submenu = menu
      else if (!parent) top.push(menu)
      else top.push(menu) // menu hermano sin item activo: tratarlo como top-level
      stack.push({ menu, currentItem: null })
      continue
    }
    if (RX_ENDMENU.test(line)) {
      stack.pop()
      continue
    }
    const ctx = stack[stack.length - 1]
    if (!ctx) continue
    const mChev = line.match(RX_CHEVRON)
    if (mChev) {
      if (ctx.currentItem) {
        const cmd = mChev[1].replace(/\{ENTER\}|\{CLEAR\}/gi, '').trim()
        if (cmd) ctx.currentItem.comandos.push(cmd)
      }
      continue
    }
    const mAssign = line.match(RX_ASSIGN)
    if (mAssign) {
      if (ctx.currentItem) ctx.currentItem.assigns.push('[' + mAssign[1] + ']=' + mAssign[2].trim())
      continue
    }
    const mGoto = line.match(RX_GOTO)
    if (mGoto) {
      if (ctx.currentItem) ctx.currentItem.target = mGoto[1]
      continue
    }
    if (RX_KEYWORD.test(line)) continue
    const text = line.trim()
    // item real = tiene letras; separadores ("-", lineas de guiones, ^R*, etc.) NO son ramas
    if (text && /[A-Za-zÁÉÍÓÚÑáéíóúñ]{2,}/.test(text) && !/^["'«»\-=_*.\s]+$/.test(text)) {
      ctx.currentItem = { label: text, comandos: [], assigns: [], target: null, submenu: null }
      ctx.menu.items.push(ctx.currentItem)
    }
  }
  // limpiar menus vacios
  const prune = (ms) => ms.filter((m) => m.items.length)
  return prune(top)
}

function renderMenuItems(items, indent, L) {
  items.slice(0, 14).forEach((it) => {
    let det = ''
    if (it.comandos.length) {
      const sample = it.comandos.slice(0, 3).join(' · ')
      det = ` · ${it.comandos.length} comandos fijos de la rama: ${sample}${it.comandos.length > 3 ? ` (+${it.comandos.length - 3} en el fuente)` : ''}`
    } else if (it.assigns.length) det = ' · asigna ' + it.assigns.join(', ')
    else if (it.target) det = ' → GOTO ' + it.target
    if (it.submenu) {
      L.push(`${indent}▸ «${it.label}» → submenú «${it.submenu.title}» (${it.submenu.items.length} ramas):`)
      renderMenuItems(it.submenu.items, indent + '    ', L)
    } else {
      L.push(`${indent}▸ «${it.label}»${det}`)
    }
  })
  if (items.length > 14) L.push(`${indent}… (+${items.length - 14} ramas)`)
}

/* === API principal: draft compacto y serializable === */
export function buildFormsDraft(source, filename) {
  try {
    const text = preprocessSst(normalizeReplacementChars(String(source || '')))
    const { ir, dataJson } = runChain(text, filename || 'script.sst')

    // Los MENU llegan del vendor como pseudo-forms (select_menu/button_menu) y las
    // ventanas de validacion son ruido (el runtime Boston ya valida mandatory):
    // NO van como forms al prompt — los menus se reportan aparte y las ramas se
    // modelan como data.json.actions.
    const allForms = dataJson.forms || []
    const tieneInputs = (f) => (f.fields || []).some((fl) => fl.typedata !== 'label' && fl.visible !== false && !fl.displayOnly && !/_menu$/.test(String(fl.typedata || '')))
    // validation SIN inputs = ventana de error (ruido); validation CON inputs = form mal clasificado → se conserva
    const validaciones = allForms.filter((f) => f.windowType === 'validation' && !tieneInputs(f)).map((f) => String(f.title || '').trim())
    const forms = allForms
      .filter((f) => f.windowType !== 'menu' && !(f.windowType === 'validation' && !tieneInputs(f)))
      .slice(0, MAX_FORMS)
      .map((f) => ({
        title: String(f.title || '').trim(),
        windowType: f.windowType || 'form',
        fields: (f.fields || [])
          .filter((fl) => !/_menu$/.test(String(fl.typedata || fl.type || '')))
          .slice(0, MAX_FIELDS_POR_FORM)
          .map(mapField),
        totalFields: (f.fields || []).length,
      }))

    const botones = Object.values(ir.buttonDefinitions || {}).map((b) => ({ id: b.button, label: b.label }))

    // menus: PRIMERO nuestro scanner (cubre comandos-inline, asignaciones y anidados);
    // el del vendor queda de fallback para estilos que el scan no capture.
    let menus = scanMenus(text).slice(0, 4)
    if (!menus.length) {
      menus = (ir.menus || []).filter((m) => !m.parentId).slice(0, 4).map((m) => ({
        title: String(m.title || '').trim(),
        items: (m.items || []).slice(0, 12).map((it) => ({ label: String(it.label || it.text || '').trim(), comandos: [], assigns: [], target: null, submenu: null })).filter((it) => it.label),
      }))
    }

    // REGLA DE ROLES: un INPUT visible de WINDOW es user_input SALVO que la
    // variable sea target de READ (read_display/read_only/read_prefilled = la
    // llena el host → mostrar como dato, no como input). La clasificacion
    // 'display' que aparece solo por usarse en comandos NO degrada inputs.
    const claseDe = (name) => {
      const e = (ir.variables || {})[name] || (ir.variables || {})[Object.keys(ir.variables || {}).find((k) => k.toUpperCase() === String(name).toUpperCase()) || '']
      return e ? String(e.classification || '') : ''
    }
    const windowInputs = new Set()
    forms.forEach((f) => f.fields.forEach((fl) => {
      if (!fl.variable || !fl.visible || fl.readonly || fl.control === 'label' || fl.control === 'button' || fl.control === 'checkbox_group') return
      const c = claseDe(fl.variable)
      if (/^read_/.test(c)) {
        fl.readonly = true
        fl.rol = c
      } else {
        fl.rol = 'user_input'
        windowInputs.add(String(fl.variable).toUpperCase())
      }
    }))
    // re-clasificar windowType DESPUES del refinamiento de roles (la base no lo sabia):
    // sin inputs editables + botones → confirmation; sin nada editable → message
    forms.forEach((f) => {
      if (f.windowType !== 'form') return
      const editables = f.fields.filter((fl) => fl.rol === 'user_input')
      const tieneBotones = f.fields.some((fl) => fl.control === 'button') || botones.length > 0
      if (!editables.length) f.windowType = tieneBotones ? 'confirmation' : 'message'
    })
    const roles = { user_input: [], read_display: [], read_only: [], read_prefilled: [], system: [], unused: 0 }
    for (const [name, v] of Object.entries(ir.variables || {})) {
      const up = String(name).toUpperCase()
      if (windowInputs.has(up)) { roles.user_input.push(name); continue }
      const c = v.classification || ''
      if (c === 'unused') roles.unused++
      else if (roles[c]) roles[c].push(name)
    }

    const totalCampos = forms.reduce((acc, f) => acc + f.totalFields, 0)
    if (!forms.length && !menus.length && !botones.length) {
      return { ok: false, error: 'sin ventanas/menus detectables', forms: [], menus: [], botones: [], roles }
    }
    return { ok: true, forms, menus, botones, roles, validaciones, totalCampos, stats: { forms: forms.length, campos: totalCampos, botones: botones.length, menus: menus.length } }
  } catch (e) {
    return { ok: false, error: e && e.message ? e.message : String(e), forms: [], menus: [], botones: [], roles: null }
  }
}

/* === Bloque de prompt para Agente 1/2/3 === */
export function formsDraftToPromptBlock(d) {
  if (!d || !d.ok) return ''
  const L = []
  L.push('=== FORMULARIOS_DETECTADOS (layout REAL extraído de las WINDOW/MENU — determinístico, sin IA) ===')
  L.push('Esto es la VERDAD del formulario (labels posicionales exactos, control y rol por uso). No re-derives el layout: usalo.')
  d.forms.forEach((f, i) => {
    L.push(`FORM ${i + 1} «${f.title || '(sin título)'}» · tipo: ${f.windowType} · ${f.totalFields} elementos`)
    f.fields.forEach((fl) => {
      const ctl = fl.control + (fl.mandatory ? '*' : '')
      const ops = fl.opciones ? ` · opciones: ${fl.opciones.slice(0, MAX_OPCIONES).join(' | ')}${fl.opciones.length > MAX_OPCIONES ? ` (+${fl.opciones.length - MAX_OPCIONES})` : ''}` : ''
      const rol = fl.rol ? ` · rol:${fl.rol}` : ''
      if (fl.control === 'checkbox_group') {
        L.push(`  • GRUPO DE CHECKBOXES: ${(fl.miembros || []).join(' · ')} (cada uno → typedata checkbox)`)
        return
      }
      if (fl.control === 'button') L.push(`  • BOTÓN «${fl.label}» → candidato a data.json.actions`)
      else if (fl.control === 'label') L.push(`  • label «${fl.label}»${fl.variable ? ` [${fl.variable}]` : ''}${rol}`)
      else L.push(`  • [${fl.variable}] «${fl.label}» · ${ctl}${fl.maxlength ? ` · max:${fl.maxlength}` : ''}${ops}${rol}`)
    })
  })
  if (d.botones.length) L.push('BOTONES DEFINIDOS: ' + d.botones.map((b) => `${b.id}="${b.label}"`).join(' · '))
  d.menus.forEach((m) => {
    if (m.items && m.items.length) {
      L.push(`MENÚ «${m.title}» (${m.items.length} ramas) → modelar como data.json.actions (UNA action por rama; comandos fijos de la rama = commands de su action):`)
      renderMenuItems(m.items, '  ', L)
    } else {
      L.push(`MENÚ «${m.title}»: ⚠ OPCIONES NO DETECTADAS — derivá las ramas del FUENTE (labels/GOTO/bloques) y modelalas como data.json.actions`)
    }
  })
  if (d.validaciones && d.validaciones.length) L.push('VENTANA(S) DE VALIDACIÓN: «' + d.validaciones.join('» «') + '» — el runtime ya valida mandatory: NO generar forms para esto.')
  if (d.roles) {
    const r = d.roles
    if (r.user_input.length) L.push('user_input (campos REALES del vendedor): ' + r.user_input.join(', '))
    if (r.read_display.length) L.push('read_display (se muestran, NO escriben): ' + r.read_display.join(', '))
    if (r.read_only.length || r.read_prefilled.length) L.push('read_only/prefilled (datos internos): ' + r.read_only.concat(r.read_prefilled).join(', '))
    if (r.unused) L.push(`unused: ${r.unused} variables sin uso real — NO generar campos para ellas`)
  }
  L.push('REGLAS: user_input → field visible con su label EXACTO; read_* → dato interno (jamás field que escribe); windowType confirmation/message con botones → data.json.actions (NUNCA fields); selects → datasource con las opciones EXACTAS; MENÚ → UNA action por rama (datos fijos de cada rama SOLO en los commands de su action — jamás como fields ocultos con dato, emitirían TODAS las ramas juntas); typedata button_menu/select_menu NO EXISTEN en el runtime: prohibidos.')
  let out = L.join('\n')
  if (out.length > MAX_BLOCK_CHARS) out = out.slice(0, MAX_BLOCK_CHARS - 12) + '\n…(recortado)'
  return out
}
